// useApi.ts
import { toast } from 'vue3-toastify'
import { getApiRequestTimeoutMs } from '@/utils/apiRequestTimeouts'

export const useApi = () => {
    const config = useRuntimeConfig()
    const token = useCookie('token')

    // L'app Nuxt est configurée avec baseURL: '/admin', donc tous les endpoints API
    // du serveur sont automatiquement préfixés par /admin
    const baseURL = '/api/proxy'

    const AUTH_TOAST_ID = 'auth-expired'
    const AUTH_TOAST_COOLDOWN_MS = 4000
    let lastAuthToastAt = 0
    let lastAuthRedirectAt = 0
    const AUTH_REDIRECT_COOLDOWN_MS = 4000
    let authRedirectInProgress = false

    const redirectToPostLoginPage = () => {
        if (!process.client || authRedirectInProgress) return
        authRedirectInProgress = true
        const now = Date.now()
        lastAuthRedirectAt = now
        try {
            const navigationResult = navigateTo('/', { replace: true, external: false })
            if (navigationResult && typeof navigationResult === 'object' && 'then' in navigationResult) {
                navigationResult
                    .then(() => { setTimeout(() => { authRedirectInProgress = false }, AUTH_REDIRECT_COOLDOWN_MS) })
                    .catch(() => {
                        if (typeof window !== 'undefined') window.location.href = '/'
                        setTimeout(() => { authRedirectInProgress = false }, AUTH_REDIRECT_COOLDOWN_MS)
                    })
            } else {
                if (typeof window !== 'undefined') window.location.href = '/'
                setTimeout(() => { authRedirectInProgress = false }, AUTH_REDIRECT_COOLDOWN_MS)
            }
        } catch {
            if (typeof window !== 'undefined') window.location.href = '/'
            setTimeout(() => { authRedirectInProgress = false }, AUTH_REDIRECT_COOLDOWN_MS)
        }
    }

    const handleAuthError = (status?: number, data?: any, error?: any) => {
        // Extraire le statut depuis différentes sources possibles
        const errorStatus = status || error?.status || error?.statusCode || error?.response?.status
        
        const code = data?.error || data?.code || error?.data?.error || error?.data?.code
        const message = (data?.message ?? data?.data?.message ?? error?.data?.message ?? error?.data?.data?.message ?? '').toString().toLowerCase()

        // IMPORTANT:
        // Un 403 API ne doit PAS déclencher une navigation globale.
        // Sinon, un appel secondaire (ex: Google Calendar / permissions) peut renvoyer 403
        // et provoquer un retour aléatoire à l'accueil pendant qu'on utilise une modale.
        // La décision d'afficher un message / rediriger appartient au composant ou aux middlewares de route.
        const isForbidden = errorStatus === 403 || message.includes('accès non autorisé') || message.includes('non autorisé')
        if (isForbidden) return

        if (
            errorStatus === 401 ||
            code === 'AUTH_TOKEN_INVALID' ||
            code === 'AUTH_TOKEN_MISSING'
        ) {
            // Nettoyer complètement la session
            token.value = null

            // Nettoyer le store d'authentification
            const authStore = useAuthStore()
            authStore.logUserOut()

            // Nettoyer le store de profil
            const profileStore = useProfileStore()
            profileStore.clearProfile()

            try {
                const now = Date.now()
                if ((now - lastAuthToastAt) > AUTH_TOAST_COOLDOWN_MS) {
                    lastAuthToastAt = now
                    toast.error('Session expirée. Veuillez vous reconnecter.', { toastId: AUTH_TOAST_ID })
                }
            } catch (_) {}

            const now = Date.now()
            if (!authRedirectInProgress && (now - lastAuthRedirectAt) > AUTH_REDIRECT_COOLDOWN_MS) {
                authRedirectInProgress = true
                lastAuthRedirectAt = now
                
                // Utiliser navigateTo avec fallback sur window.location pour garantir la redirection
                if (process.client) {
                    try {
                        const navigationResult = navigateTo('/auth/login', { replace: true, external: false })
                        
                        // Vérifier si le résultat est une Promise avant d'appeler .then()
                        if (navigationResult && typeof navigationResult === 'object' && 'then' in navigationResult) {
                            navigationResult
                                .then(() => {
                                    setTimeout(() => { authRedirectInProgress = false }, AUTH_REDIRECT_COOLDOWN_MS)
                                })
                                .catch(() => {
                                    // Fallback sur window.location si navigateTo échoue
                                    if (typeof window !== 'undefined') {
                                        window.location.href = '/auth/login'
                                    }
                                    setTimeout(() => { authRedirectInProgress = false }, AUTH_REDIRECT_COOLDOWN_MS)
                                })
                        } else {
                            // Si navigateTo retourne autre chose qu'une Promise, utiliser window.location
                            if (typeof window !== 'undefined') {
                                window.location.href = '/auth/login'
                            }
                            setTimeout(() => { authRedirectInProgress = false }, AUTH_REDIRECT_COOLDOWN_MS)
                        }
                    } catch (err) {
                        // Fallback sur window.location si navigateTo échoue
                        if (typeof window !== 'undefined') {
                            window.location.href = '/auth/login'
                        }
                        setTimeout(() => { authRedirectInProgress = false }, AUTH_REDIRECT_COOLDOWN_MS)
                    }
                }
            }
        }
    }

    // --- helper to inject x-proxy-service header without changing component calls ---
    function injectProxyServiceHeader(options: any, serviceName: 'laravel' | 'django') {
        // Extraire les headers personnalisés de l'objet options
        const customHeaders: any = {};
        Object.keys(options).forEach(key => {
            if (key.startsWith('X-') || key === 'Authorization' || key === 'Content-Type') {
                customHeaders[key] = options[key];
                delete options[key]; // Supprimer de options pour éviter la duplication
            }
        });

        options.headers = {
            // important: keep accept and not force content-type (FormData must work)
            accept: options.headers?.accept ?? 'application/json',
            'x-proxy-service': serviceName,
            ...(token.value ? { Authorization: `Bearer ${token.value}` } : {}),
            // Préserver tous les headers personnalisés
            ...(options.headers || {}),
            ...customHeaders
        }
    }

    // api pour Django (auth)
    const apiDjangoAuth = $fetch.create({
        baseURL,
        async onRequest({ options }: any) {
            // ne pas écraser headers fournis dans l'appel, juste injecter
            injectProxyServiceHeader(options, 'django')
        },
        onResponseError: (ctx: any) => {
            handleAuthError(ctx?.response?.status, ctx?.response?._data, ctx?.error)
        },
        onRequestError: (ctx: any) => {
            // Gérer les erreurs réseau qui peuvent survenir avant d'atteindre le serveur
            // Si c'est une erreur 401 dans la réponse d'erreur, la traiter
            if (ctx?.error?.status === 401 || ctx?.error?.statusCode === 401) {
                handleAuthError(401, ctx?.error?.data, ctx?.error)
            }
        }
    })

    // api pour Django guest
    const apiDjangoGuest = $fetch.create({
        baseURL,
        async onRequest({ options }: any) {
            options.headers = {
                ...(options.headers || {}),
                accept: options.headers?.accept ?? 'application/json',
                'x-proxy-service': 'django'
            }
        },
        onRequestError: (ctx: any) => {

        }
    })

    function applyRequestTimeout(options: any, request: string | Request, method?: string) {
        if (options.timeout != null) return
        const path = typeof request === 'string' ? request : request.url
        options.timeout = getApiRequestTimeoutMs(path, (method || options.method || 'GET').toUpperCase())
    }

    // api pour Laravel
    const apiData = $fetch.create({
        baseURL,
        async onRequest({ request, options }: any) {
            injectProxyServiceHeader(options, 'laravel')
            applyRequestTimeout(options, request, options.method)
        },
        onResponseError: (ctx: any) => {
            handleAuthError(ctx?.response?.status, ctx?.response?._data, ctx?.error)
            
            // Extraire le message d'erreur du backend Laravel depuis data.message
            // et le placer aussi dans response._data pour compatibilité avec le code existant
            if (ctx?.response?._data?.data?.message) {
                // Copier le message depuis data.message vers _data.message pour faciliter l'accès
                if (!ctx.response._data.message || ctx.response._data.message === ctx.response._data.statusMessage) {
                    ctx.response._data.message = ctx.response._data.data.message
                }
            }
        },
        onRequestError: (ctx: any) => {
            // Gérer les erreurs réseau qui peuvent survenir avant d'atteindre le serveur
            // Si c'est une erreur 401 dans la réponse d'erreur, la traiter
            if (ctx?.error?.status === 401 || ctx?.error?.statusCode === 401) {
                handleAuthError(401, ctx?.error?.data, ctx?.error)
            }
        }
    })

    return { apiDjangoAuth, apiDjangoGuest, apiData }
}
